import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSecurityNotifications } from '../contexts/SecurityNotificationsContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, AlertCircle, Info, RefreshCw, Bell, Settings,
  Package, Users, ChefHat, Wine, CreditCard, Eye, CheckCircle,
  Clock, TrendingUp, History, Send, Loader2, XCircle, Volume2, VolumeX, Zap
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs par sévérité
const SEVERITY_COLORS = {
  critical: { bg: 'bg-red-500', text: 'text-white', border: 'border-red-500', light: 'bg-red-50' },
  warning: { bg: 'bg-orange-500', text: 'text-white', border: 'border-orange-500', light: 'bg-orange-50' },
  info: { bg: 'bg-blue-500', text: 'text-white', border: 'border-blue-500', light: 'bg-blue-50' }
};

const SEVERITY_LABELS = {
  critical: 'Critique',
  warning: 'Attention',
  info: 'Info'
};

// Icônes par catégorie
const CATEGORY_ICONS = {
  caisse: CreditCard,
  stock: Package,
  cuisine: ChefHat,
  bar: Wine,
  rh: Users,
  fraude: AlertTriangle
};

const CATEGORY_LABELS = {
  caisse: 'Caisse',
  stock: 'Stock',
  cuisine: 'Cuisine',
  bar: 'Bar',
  rh: 'RH',
  fraude: 'Fraude'
};

// Composant AlertCard
const AlertCard = ({ alert, onResolve }) => {
  const Icon = CATEGORY_ICONS[alert.category] || AlertTriangle;
  const colors = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
  
  return (
    <div className={`p-4 border-l-4 ${colors.border} ${colors.light} rounded-lg`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${colors.bg}`}>
            <Icon className={`w-4 h-4 ${colors.text}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium">{alert.title}</h4>
              <Badge className={`${colors.bg} ${colors.text}`}>
                {SEVERITY_LABELS[alert.severity]}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{alert.message}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {new Date(alert.detected_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <Badge variant="outline">
                {CATEGORY_LABELS[alert.category] || alert.category}
              </Badge>
            </div>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onResolve(alert.id)}
          className="shrink-0"
        >
          <CheckCircle className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// Composant CategoryStat
const CategoryStat = ({ category, stats }) => {
  const Icon = CATEGORY_ICONS[category] || AlertTriangle;
  const hasAlerts = stats.total > 0;
  
  return (
    <div className={`p-3 rounded-lg border ${hasAlerts ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${hasAlerts ? 'text-orange-600' : 'text-green-600'}`} />
          <span className="font-medium text-sm">{CATEGORY_LABELS[category] || category}</span>
        </div>
        {stats.critical > 0 && (
          <Badge className="bg-red-500 text-white text-xs">{stats.critical}</Badge>
        )}
      </div>
      <div className="flex gap-2 text-xs">
        {stats.critical > 0 && <span className="text-red-600">{stats.critical} critique(s)</span>}
        {stats.warning > 0 && <span className="text-orange-600">{stats.warning} attention</span>}
        {stats.total === 0 && <span className="text-green-600">OK</span>}
      </div>
    </div>
  );
};

export default function SecurityPage() {
  const { token } = useAuth();
  const { 
    scannerStatus, 
    permission, 
    requestPermission, 
    triggerManualScan,
    playAlertSound 
  } = useSecurityNotifications();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [config, setConfig] = useState(null);
  
  // Config form state
  const [configForm, setConfigForm] = useState({
    register_inactive_minutes: 30,
    stock_variance_percent: 5,
    kitchen_delay_minutes: 15,
    bar_delay_minutes: 10,
    shift_late_minutes: 10,
    auto_send_alerts: true
  });

  const fetchData = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dashboardRes, historyRes, configRes] = await Promise.all([
        fetch(`${API}/api/security/dashboard`, { headers }),
        fetch(`${API}/api/security/history?days=7`, { headers }),
        fetch(`${API}/api/security/config`, { headers })
      ]);
      
      if (dashboardRes.ok) setDashboard(await dashboardRes.json());
      if (historyRes.ok) setHistory((await historyRes.json()).scans || []);
      if (configRes.ok) {
        const cfg = await configRes.json();
        setConfig(cfg);
        if (cfg.thresholds) {
          setConfigForm({
            register_inactive_minutes: cfg.thresholds.register_inactive_minutes || 30,
            stock_variance_percent: cfg.thresholds.stock_variance_percent || 5,
            kitchen_delay_minutes: cfg.thresholds.kitchen_delay_minutes || 15,
            bar_delay_minutes: cfg.thresholds.bar_delay_minutes || 10,
            shift_late_minutes: cfg.thresholds.shift_late_minutes || 10,
            auto_send_alerts: cfg.auto_send_alerts ?? true
          });
        }
      }
      
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Erreur lors du chargement des données de sécurité');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh toutes les 30 secondes
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunScan = async (sendAlerts = false) => {
    setScanning(true);
    try {
      const response = await fetch(
        `${API}/api/security/check-now?send_alerts=${sendAlerts}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchData();
      } else {
        toast.error('Erreur lors du scan');
      }
    } catch (error) {
      console.error('Error running scan:', error);
      toast.error('Erreur lors du scan de sécurité');
    } finally {
      setScanning(false);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      const response = await fetch(
        `${API}/api/security/resolve/${alertId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Alerte marquée comme résolue');
        fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la résolution');
    }
  };

  const handleSaveConfig = async () => {
    try {
      const response = await fetch(`${API}/api/security/config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configForm)
      });
      
      if (response.ok) {
        toast.success('Configuration sauvegardée');
        fetchData();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const statusColor = dashboard?.summary?.status === 'critical' ? 'text-red-600' :
                      dashboard?.summary?.status === 'warning' ? 'text-orange-600' : 'text-green-600';
  const statusBg = dashboard?.summary?.status === 'critical' ? 'bg-red-50' :
                   dashboard?.summary?.status === 'warning' ? 'bg-orange-50' : 'bg-green-50';

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="security-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className={`w-7 h-7 ${statusColor}`} />
            Contrôle et Sécurité
          </h1>
          <p className="text-muted-foreground">
            Surveillance en temps réel et détection d'anomalies
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scanner Status Badge */}
          {scannerStatus && (
            <Badge 
              variant="outline" 
              className={`${scannerStatus.running ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-50'}`}
            >
              <Zap className={`w-3 h-3 mr-1 ${scannerStatus.running ? 'text-green-500' : ''}`} />
              Auto: {scannerStatus.scan_interval_minutes}min
              {scannerStatus.silent_mode_active && (
                <VolumeX className="w-3 h-3 ml-1 text-orange-500" />
              )}
            </Badge>
          )}
          
          {/* Notification Permission */}
          {permission !== 'granted' && (
            <Button
              variant="outline"
              size="sm"
              onClick={requestPermission}
              className="text-blue-600 border-blue-300"
            >
              <Volume2 className="w-4 h-4 mr-1" />
              Activer sons
            </Button>
          )}
          
          <Button 
            variant="outline" 
            onClick={() => handleRunScan(false)}
            disabled={scanning}
          >
            {scanning ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            Scanner
          </Button>
          <Button 
            onClick={() => handleRunScan(true)}
            disabled={scanning}
            className="bg-red-600 hover:bg-red-700"
          >
            <Send className="w-4 h-4 mr-2" />
            Scanner + Alerter
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      <Card className={`${statusBg} border-2 ${
        dashboard?.summary?.status === 'critical' ? 'border-red-300' :
        dashboard?.summary?.status === 'warning' ? 'border-orange-300' : 'border-green-300'
      }`}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {dashboard?.summary?.status === 'critical' ? (
                <AlertCircle className="w-8 h-8 text-red-600" />
              ) : dashboard?.summary?.status === 'warning' ? (
                <AlertTriangle className="w-8 h-8 text-orange-600" />
              ) : (
                <CheckCircle className="w-8 h-8 text-green-600" />
              )}
              <div>
                <h3 className={`text-lg font-bold ${statusColor}`}>
                  {dashboard?.summary?.status === 'critical' ? 'Alertes Critiques Détectées' :
                   dashboard?.summary?.status === 'warning' ? 'Attention Requise' : 'Système OK'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {dashboard?.summary?.total_alerts || 0} alerte(s) active(s)
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 text-center">
              <div className="px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-red-600">{dashboard?.summary?.critical || 0}</p>
                <p className="text-xs text-muted-foreground">Critiques</p>
              </div>
              <div className="px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-orange-600">{dashboard?.summary?.warning || 0}</p>
                <p className="text-xs text-muted-foreground">Attention</p>
              </div>
              <div className="px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-blue-600">{dashboard?.summary?.info || 0}</p>
                <p className="text-xs text-muted-foreground">Info</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Tableau de bord</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Historique</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Configuration</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Category Overview */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(dashboard?.by_category || {}).map(([category, stats]) => (
              <CategoryStat key={category} category={category} stats={stats} />
            ))}
          </div>

          {/* Alerts List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Alertes Actives
              </CardTitle>
              <CardDescription>
                Dernière mise à jour: {dashboard?.generated_at 
                  ? new Date(dashboard.generated_at).toLocaleTimeString('fr-FR')
                  : '-'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard?.alerts?.length > 0 ? (
                  dashboard.alerts.map((alert) => (
                    <AlertCard 
                      key={alert.id} 
                      alert={alert}
                      onResolve={handleResolveAlert}
                    />
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                    <h3 className="font-medium">Aucune alerte</h3>
                    <p className="text-sm text-muted-foreground">
                      Tous les systèmes fonctionnent normalement
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique des Scans</CardTitle>
              <CardDescription>7 derniers jours</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.length > 0 ? (
                  history.map((scan) => (
                    <div key={scan.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">
                            {new Date(scan.executed_at).toLocaleDateString('fr-FR')}
                            {' '}
                            {new Date(scan.executed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Par {scan.executed_by_name || 'Système'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">
                          {scan.total_alerts} alertes
                        </Badge>
                        {scan.critical_count > 0 && (
                          <Badge className="bg-red-500 text-white">
                            {scan.critical_count} critiques
                          </Badge>
                        )}
                        {scan.alerts_sent && (
                          <Badge className="bg-blue-500 text-white">
                            <Send className="w-3 h-3 mr-1" />
                            Envoyé
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Aucun scan enregistré
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Seuils d'Alerte</CardTitle>
              <CardDescription>
                Configurez les seuils de détection des anomalies
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Caisse */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Caisse inactive (minutes)
                  </Label>
                  <Input
                    type="number"
                    value={configForm.register_inactive_minutes}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      register_inactive_minutes: parseInt(e.target.value) || 30
                    })}
                    min={5}
                    max={240}
                  />
                  <p className="text-xs text-muted-foreground">
                    Alerte si caisse ouverte sans vente depuis ce délai
                  </p>
                </div>

                {/* Stock */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Écart de stock (%)
                  </Label>
                  <Input
                    type="number"
                    value={configForm.stock_variance_percent}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      stock_variance_percent: parseFloat(e.target.value) || 5
                    })}
                    min={1}
                    max={50}
                    step={0.5}
                  />
                  <p className="text-xs text-muted-foreground">
                    Alerte si écart entre stock théorique et réel
                  </p>
                </div>

                {/* Cuisine */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ChefHat className="w-4 h-4" />
                    Retard cuisine (minutes)
                  </Label>
                  <Input
                    type="number"
                    value={configForm.kitchen_delay_minutes}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      kitchen_delay_minutes: parseInt(e.target.value) || 15
                    })}
                    min={5}
                    max={60}
                  />
                  <p className="text-xs text-muted-foreground">
                    Alerte si commande en attente depuis ce délai
                  </p>
                </div>

                {/* Bar */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Wine className="w-4 h-4" />
                    Retard bar (minutes)
                  </Label>
                  <Input
                    type="number"
                    value={configForm.bar_delay_minutes}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      bar_delay_minutes: parseInt(e.target.value) || 10
                    })}
                    min={3}
                    max={30}
                  />
                  <p className="text-xs text-muted-foreground">
                    Alerte si commande bar en attente
                  </p>
                </div>

                {/* RH */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Retard pointage (minutes)
                  </Label>
                  <Input
                    type="number"
                    value={configForm.shift_late_minutes}
                    onChange={(e) => setConfigForm({
                      ...configForm,
                      shift_late_minutes: parseInt(e.target.value) || 10
                    })}
                    min={5}
                    max={60}
                  />
                  <p className="text-xs text-muted-foreground">
                    Alerte si employé en retard de plus de X minutes
                  </p>
                </div>

                {/* Auto-envoi */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Alertes automatiques
                  </Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={configForm.auto_send_alerts}
                      onCheckedChange={(checked) => setConfigForm({
                        ...configForm,
                        auto_send_alerts: checked
                      })}
                    />
                    <span className="text-sm text-muted-foreground">
                      {configForm.auto_send_alerts ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Envoyer automatiquement les alertes critiques
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button onClick={handleSaveConfig} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Sauvegarder la configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
